import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WarningIcon from "@mui/icons-material/Warning";
import PersonAvatar from "../home/schedule_components/PersonAvatar";
import SchoolIcon from "@mui/icons-material/School";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ChecklistIcon from "@mui/icons-material/Checklist";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LoadSpinner from '../../../utilities/LoadSpinner';
import { getRightworkAPI } from '../requests/utilities/requests';
import {
  API_get_learning_tracks,
  API_get_learning_form_submission_for_grading,
} from '../../../constants';
import { getInitials } from '../requests/utilities/helpers';
import FormGradeModal from './FormGradeModal';

function CheckpointReviewModal(props) {

  // Get icon component and colors for block type (matching RecordsDisplay pattern)
  function getBlockTypeDisplay(blockType) {
    switch (blockType) {
      case "quiz":
        return {
          icon: QuizOutlinedIcon,
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600",
          label: "Failed Quiz",
        };
      case "checkpoint":
        return {
          icon: ChecklistIcon,
          bgColor: "bg-red-100",
          iconColor: "text-red-600",
          label: "Checkpoint",
        };
      case "manager_input":
        return {
          icon: RateReviewOutlinedIcon,
          bgColor: "bg-amber-100",
          iconColor: "text-amber-600",
          label: "Manager Input",
        };
      case "manager_form":
        return {
          icon: AssignmentOutlinedIcon,
          bgColor: "bg-violet-100",
          iconColor: "text-violet-600",
          label: "Manager Form",
        };
      default:
        return {
          icon: WarningIcon,
          bgColor: "bg-gray-100",
          iconColor: "text-gray-600",
          label: "Unknown",
        };
    }
  }

  // May be undefined during the parent's initial load; the pending-grades count
  // can open this modal before blocked enrollments have resolved.
  const blockedEmployees = props.filteredEmployeesBlocked || [];

  // Pending form grades are a flat list from the store; keep a local copy so we
  // can drop a row the moment it's graded without waiting for the parent refetch.
  const [localGrades, setLocalGrades] = useState(props.pendingFormGrades || []);
  useEffect(() => {
    setLocalGrades(props.pendingFormGrades || []);
  }, [props.pendingFormGrades]);

  // The submission package fetched for in-place grading (record + submission),
  // plus the key of the row it belongs to so we can remove it on save.
  const [gradeData, setGradeData] = useState(null);
  const [gradeLoadingKey, setGradeLoadingKey] = useState(null);

  // Section is part of the key: the same form can be embedded in more than one
  // section, so (form, store_team_member) alone isn't unique across rows.
  const gradeRowKey = (g) =>
    `${g.form_uuid}:${g.section_uuid}:${g.store_team_member_id}`;

  const openGrade = async (g) => {
    if (gradeLoadingKey) return;
    const key = gradeRowKey(g);
    setGradeLoadingKey(key);
    try {
      const res = await getRightworkAPI(
        API_get_learning_form_submission_for_grading,
        {
          company_id: props.homeCompany,
          learning_form_uuid: g.form_uuid,
          section_uuid: g.section_uuid,
          store_team_member_id: g.store_team_member_id,
        }
      );
      const payload = res.data?.redux?.payload;
      if (payload?.submission) {
        setGradeData({
          record: payload.record,
          submission: payload.submission,
          rowKey: key,
        });
      }
    } catch (error) {
      // request layer surfaces the error toast
    } finally {
      setGradeLoadingKey(null);
    }
  };

  const handleGraded = () => {
    if (gradeData?.rowKey) {
      setLocalGrades((prev) =>
        prev.filter((g) => gradeRowKey(g) !== gradeData.rowKey)
      );
    }
    setGradeData(null);
    if (props.onPendingGradeResolved) props.onPendingGradeResolved();
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    maxHeight: "96vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  return (
    <Modal
      open={props.open}
      onClose={() => {
        props.handleClose();
      }}
      aria-labelledby="checkpoint-modal-title"
      aria-describedby="checkpoint-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-6 mx-0 mt-0 mb-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <WarningIcon className="text-amber-600" style={{ fontSize: "20px" }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900">
                  Review Pending Actions
                </h3>
                <p className="text-sm text-slate-600">
                  Review checkpoints, manager inputs, quiz retakes, and grade submitted forms
                </p>
              </div>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
              onClick={() => {
                props.handleClose();
              }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <div className="w-full flex items-center gap-4">
                <div className="w-[22%] pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</div>
                <div className="w-[18%] pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Track</div>
                <div className="w-[18%] pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Module</div>
                <div className="w-[22%] pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Section</div>
                <div className="w-[14%] pr-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</div>
                <div className="w-[6%] text-xs font-semibold text-gray-600 uppercase tracking-wider text-right">Action</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 bg-white">
              {blockedEmployees.map((employee, index) => {
                const learningTrackEnrollments = employee?.user?.learning_track_enrollments || [];
                return learningTrackEnrollments.map((enrollment, i) => {
                  const typeDisplay = getBlockTypeDisplay(enrollment.blocked_by_type);
                  const IconComponent = typeDisplay.icon;

                  return (
                    <div
                      key={`${index}-${i}`}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                    >
                      <div className="w-[22%] pr-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <PersonAvatar
                              name={`${employee.first_name} ${employee.last_name}`}
                              bgColor={
                                props.employeeAvatarColorById?.[employee.id] ||
                                "bg-slate-600"
                              }
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 break-words">
                              {employee.first_name} {employee.last_name}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="w-[18%] pr-4">
                        <p className="text-sm text-gray-700 break-words">
                          {enrollment.track.name}
                        </p>
                      </div>
                      <div className="w-[18%] pr-4">
                        <p className="text-sm text-gray-700 break-words">
                          {enrollment.next_module?.name || "—"}
                        </p>
                      </div>
                      <div className="w-[22%] pr-4">
                        <p className="text-sm text-gray-700 break-words">
                          {enrollment.next_section?.name || "—"}
                        </p>
                      </div>
                      <div className="w-[14%] pr-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 ${typeDisplay.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <IconComponent
                              className={typeDisplay.iconColor}
                              style={{ fontSize: "16px" }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900 break-words">
                            {typeDisplay.label}
                          </span>
                        </div>
                      </div>
                      <div className="w-[6%] flex justify-end">
                        <Tooltip
                          title={`Review ${enrollment.track.name} for ${employee.first_name}`}
                        >
                          <button
                            className="w-8 h-8 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                            onClick={() => {
                              props.handleClose()
                              props.setCurrentTrackEmployeeName(`${employee.first_name} ${employee.last_name}`);
                              props.setCurrentTrack(enrollment);
                              props.setCurrentTrackUUID(enrollment.track.uuid);
                              props.fetchTrackDetails(true, enrollment.track.uuid, employee.id);
                              props.setShowCompleteTrack(true);
                              props.setSelectedEmployee(employee);
                            }}
                          >
                            <PlayArrowIcon style={{ fontSize: "18px" }} />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  );
                });
              })}

              {/* Pending form grades — a manager to-do whether or not the form
                  gates the learner. Clicking opens the grade modal in place. */}
              {localGrades.map((g, gi) => {
                const key = gradeRowKey(g);
                const loading = gradeLoadingKey === key;
                return (
                  <div
                    key={`grade-${gi}`}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                  >
                    <div className="w-[22%] pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <PersonAvatar
                            name={g.employee_name || ""}
                            bgColor={
                              props.employeeAvatarColorById?.[
                                g.store_team_member_id
                              ] || "bg-slate-600"
                            }
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 break-words">
                            {g.employee_name || "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="w-[18%] pr-4">
                      <p className="text-sm text-gray-700 break-words">
                        {g.track_name || "—"}
                      </p>
                    </div>
                    <div className="w-[18%] pr-4">
                      <p className="text-sm text-gray-700 break-words">
                        {g.module_name || "—"}
                      </p>
                    </div>
                    <div className="w-[22%] pr-4">
                      <p className="text-sm text-gray-700 break-words">
                        {g.section_name || "—"}
                      </p>
                    </div>
                    <div className="w-[14%] pr-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FactCheckOutlinedIcon
                            className="text-violet-600"
                            style={{ fontSize: "16px" }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 break-words">
                          Needs grading
                        </span>
                      </div>
                    </div>
                    <div className="w-[6%] flex justify-end">
                      <Tooltip
                        title={`Grade ${g.form_name || "form"} for ${g.employee_name || "employee"}`}
                      >
                        <span>
                          <button
                            disabled={loading}
                            className="w-8 h-8 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                            onClick={() => openGrade(g)}
                          >
                            {loading ? (
                              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FactCheckOutlinedIcon style={{ fontSize: "18px" }} />
                            )}
                          </button>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}

              {blockedEmployees.length === 0 &&
                localGrades.length === 0 && (
                  <div className="px-6 py-10 text-center text-sm text-gray-500">
                    No pending actions right now.
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-5 mx-0 mb-0 mt-8 z-10">
          <div className="flex items-center justify-end">
            <button
              className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
              onClick={() => props.handleClose()}
            >
              Close
            </button>
          </div>
        </div>

        {/* In-place grading — layered over this modal so the manager never
            leaves the pending-actions list. */}
        {gradeData && (
          <FormGradeModal
            open={!!gradeData}
            onClose={() => setGradeData(null)}
            record={gradeData.record}
            submission={gradeData.submission}
            homeCompany={props.homeCompany}
            onGraded={handleGraded}
          />
        )}
      </Box>
    </Modal>
  );
}

export default CheckpointReviewModal;
